import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ArrowLeft } from 'lucide-react';
import { useStoreData } from '../hooks/useStoreData';
import { useCart } from '../contexts/CartContext';
import Header from '../components/Header';
import Cart from '../components/Cart';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

const FAQ = () => {
  const navigate = useNavigate();
  const { settings } = useStoreData();
  const { getCartCount } = useCart();
  const [openIndex, setOpenIndex] = useState(null);
  const [isCartOpen, setIsCartOpen] = useState(false);

  const faqs = [
    {
      category: "Orders & Shipping",
      questions: [
        {
          q: "How long does shipping take?",
          a: "We offer free delivery all over Pakistan. Standard shipping typically takes 3-5 business days for major cities and 5-7 business days for other areas. Express shipping is available for an additional fee."
        },
        {
          q: "Do you ship internationally?",
          a: "Currently, we only ship within Pakistan. We are working on expanding our international shipping options. Please check back soon!"
        },
        {
          q: "How can I track my order?",
          a: "Once your order is shipped, you will receive a tracking number via email and SMS. You can use this number to track your order on our courier partner's website."
        },
        {
          q: "Can I change my shipping address after placing an order?",
          a: "If your order hasn't been processed yet, please contact us immediately at " + (settings?.contactInfo?.phone || "our support number") + " and we'll update your shipping address."
        }
      ]
    },
    {
      category: "Products & Sizing",
      questions: [
        {
          q: "How do I choose the right size?",
          a: "Each product page includes a detailed size chart. Our abayas are available in S, M, L, and XL sizes. If you're between sizes, we recommend sizing up for a more comfortable fit."
        },
        {
          q: "What fabrics do you use?",
          a: "We use premium quality fabrics including Nida, Crepe, Georgette, and Korean Nida. Each product description includes detailed fabric information and care instructions."
        },
        {
          q: "Are your products true to the images shown?",
          a: "Yes, we strive to represent our products as accurately as possible. However, slight color variations may occur due to screen settings and lighting conditions."
        },
        {
          q: "Do you restock sold-out items?",
          a: "Popular items are regularly restocked. You can contact us to inquire about specific products, and we'll let you know when they'll be available again."
        }
      ]
    },
    {
      category: "Payments",
      questions: [
        {
          q: "What payment methods do you accept?",
          a: "We accept Cash on Delivery (COD), bank transfers, and online payments through JazzCash, EasyPaisa, and bank cards."
        },
        {
          q: "Is Cash on Delivery available?",
          a: "Yes, COD is available for all orders across Pakistan. A small COD handling fee may apply."
        },
        {
          q: "Are my payment details secure?",
          a: "Absolutely. We use industry-standard encryption and secure payment gateways to protect your financial information."
        }
      ]
    },
    {
      category: "Returns & Exchanges",
      questions: [
        {
          q: "What is your return policy?",
          a: "We accept returns within 7 days of delivery for unused items in original condition with tags attached. Please refer to our Exchange Policy page for complete details."
        },
        {
          q: "How do I initiate an exchange?",
          a: "Contact our customer service team at " + (settings?.contactInfo?.email || "our support email") + " with your order number and reason for exchange. We'll guide you through the process."
        },
        {
          q: "Who pays for return shipping?",
          a: "For defective or incorrect items, we cover return shipping costs. For size exchanges or change of mind, the customer is responsible for return shipping."
        },
        {
          q: "How long does the refund process take?",
          a: "Once we receive and inspect your return, refunds are processed within 5-7 business days to your original payment method."
        }
      ]
    },
    {
      category: "General",
      questions: [
        {
          q: "How can I contact customer service?",
          a: "You can reach us via phone at " + (settings?.contactInfo?.phone || "our support number") + ", email at " + (settings?.contactInfo?.email || "our support email") + ", or through our social media channels. We're here to help!"
        },
        {
          q: "Do you have a physical store?",
          a: "Our address is " + (settings?.contactInfo?.address || "available on our contact page") + ". Please call ahead to confirm visiting hours."
        },
        {
          q: "Can I customize an abaya?",
          a: "We occasionally offer customization services for bulk orders or special occasions. Please contact us with your requirements for more information."
        },
        {
          q: "Do you offer wholesale or bulk discounts?",
          a: "Yes, we offer special pricing for bulk orders. Please contact us with your requirements and we'll provide you with a custom quote."
        }
      ]
    }
  ];

  const toggleQuestion = (categoryIndex, questionIndex) => {
    const index = `${categoryIndex}-${questionIndex}`;
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="FAQ - Frequently Asked Questions | Rizzahra"
        description="Find answers to common questions about ordering, shipping, sizing, returns, and our abayas at Rizzahra. Get help with your modest fashion shopping."
        keywords="faq, shipping, returns, sizing, abaya care, customer support"
        url="/faq"
      />
      {/* Main Header */}
      <Header
        cartCount={getCartCount()}
        onCartClick={() => setIsCartOpen(true)}
        onAdminClick={() => navigate('/admin')}
      />

      {/* Page Header */}
      <header className="bg-[#4a1a1f] text-white py-12">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl font-bold uppercase tracking-wider">Frequently Asked Questions</h1>
          <p className="text-gray-300 mt-2">Find answers to common questions about our products and services</p>
        </div>
      </header>

      {/* FAQ Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          {faqs.map((category, categoryIndex) => (
            <div key={categoryIndex} className="mb-8">
              <h2 className="text-2xl font-bold text-[#4a1a1f] mb-4 uppercase tracking-wider">
                {category.category}
              </h2>
              <div className="space-y-3">
                {category.questions.map((faq, questionIndex) => {
                  const index = `${categoryIndex}-${questionIndex}`;
                  const isOpen = openIndex === index;

                  return (
                    <div key={questionIndex} className="bg-white rounded-lg shadow-sm overflow-hidden">
                      <button
                        onClick={() => toggleQuestion(categoryIndex, questionIndex)}
                        className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                      >
                        <span className="font-semibold text-gray-800 pr-4">{faq.q}</span>
                        <ChevronDown
                          size={20}
                          className={`flex-shrink-0 text-[#4a1a1f] transition-transform ${
                            isOpen ? 'transform rotate-180' : ''
                          }`}
                        />
                      </button>
                      {isOpen && (
                        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
                          <p className="text-gray-700">{faq.a}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Contact CTA */}
        <div className="max-w-4xl mx-auto mt-12 bg-[#4a1a1f] text-white rounded-lg p-8 text-center">
          <h3 className="text-2xl font-bold mb-3">Still Have Questions?</h3>
          <p className="text-gray-300 mb-6">
            Our customer service team is here to help you with any inquiries.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            {settings?.contactInfo?.phone && (
              <a
                href={`tel:${settings.contactInfo.phone}`}
                className="bg-white text-[#4a1a1f] px-6 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors"
              >
                Call Us: {settings.contactInfo.phone}
              </a>
            )}
            {settings?.contactInfo?.email && (
              <a
                href={`mailto:${settings.contactInfo.email}`}
                className="bg-transparent border-2 border-white text-white px-6 py-3 rounded-full font-semibold hover:bg-white hover:text-[#4a1a1f] transition-colors"
              >
                Email Us
              </a>
            )}
          </div>
        </div>
      </div>

      <Footer />
      <Cart isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />
    </div>
  );
};

export default FAQ;
